import { Injectable, Signal, computed, inject, signal } from '@angular/core'
import { Select, Store } from '@ngxs/store'
import { EnvironmentService } from '../../services/environment.service'
import { EncryptDecryptService } from '../../services/encrypt-decrypt.service'
import { AddFormsAction, AddStateItemAction, GenericState, RemoveAllItemsAction, RemoveStateItemAction, ResetStateAction } from './generic.state'
import { toSignal } from '@angular/core/rxjs-interop'
import { Observable } from 'rxjs'
import { LineConfigurationState } from '../../models/line-configurations.model'

@Injectable({
    providedIn: 'root'
})
export class GenericStateService {
    private store = inject(Store)
    private encryptDecryptService = inject(EncryptDecryptService)
    private environmentService = inject(EnvironmentService)

    // Create a signal , whicch can be used in computed whenever a form is set
    formUpdatedSignal = signal<any | null>(null);
    appStateUpdatedSignal = signal<any | null>(null);


    @Select(GenericState.forms) private formsSelector$: Observable<string>

    #formsSignal: Signal<string>

    private items$ = toSignal(
        this.store.select(state => state.GenericState.items)
    )

    private appStateItems$ = toSignal(
        this.store.select(state => state.GenericState.AppStateItems)
    )

    constructor() {
        this.#formsSignal = toSignal(this.formsSelector$)
    }

    setForms(forms: any) {
        this.store.dispatch(new AddFormsAction(forms));
        this.formUpdatedSignal.set(forms);
    }

    setItem(key: string, value: any) {
        this.store.dispatch(new AddStateItemAction(key, value));
        this.appStateUpdatedSignal.set({ key, value });
    }

    removeItem(key: string) {
        this.store.dispatch(new RemoveStateItemAction(key));
    }

    removeAllItems() {
        this.store.dispatch(new RemoveAllItemsAction());
    }

    getData<T>(stateKey: string): Signal<T> {
        return computed(() => {
            const encryptedData = this.store.selectSnapshot(state => state.GenericState[stateKey])
            if (!encryptedData) return null
            return this.encryptDecryptService.decrypt<T>(
                encryptedData,
                this.environmentService.sessionDecryptKey
            )
        })
    }

    getForms = computed(() => {
        return this.encryptDecryptService.decrypt<any>(this.#formsSignal(), this.environmentService.sessionDecryptKey)
    })

    getItem<T>(key: string): Signal<T | null> {
        return computed(() => {
            const items = this.appStateItems$();
            if (!items || !items[key]) return null;

            return this.encryptDecryptService.decrypt<T>(
                items[key],
                this.environmentService.sessionDecryptKey
            );
        });
    }

    getAllItems<T>(): Signal<{ [key: string]: T }> {
        return computed(() => {
            const items = this.appStateItems$();
            if (!items) return {};

            const result: { [key: string]: T } = {};
            Object.entries(items).forEach(([key, encryptedValue]) => {
                result[key] = this.encryptDecryptService.decrypt<T>(
                    encryptedValue as string,
                    this.environmentService.sessionDecryptKey
                );
            });

            return result;
        });
    }

    reset() {
        this.store.dispatch(new ResetStateAction());
    }

    getLineConfigurationState(): LineConfigurationState {
        const lineConfigurations = this.getForms()?.lineConfigurations;
        return lineConfigurations ?? null;
    }

    getConfiguredLineNumbers(): number[] {
        const state = this.getLineConfigurationState();
        if (!state) return [];
        return Object.keys(state)
            .map(key => parseInt(key.replace('line', '')))
            .filter(num => !isNaN(num))
            .sort((a, b) => a - b);
    }
}